import { useRef, useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Icon } from "@/components/ui/icon";
import { SquareLoader } from "@/components/ui/square-loader";
import { ChainOfThought, type ThoughtStep } from "@/components/chat/ChainOfThought";
import { cn } from "@/lib/utils";

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  parts?: Array<MessagePart>;
  content?: string;
  isStreaming?: boolean;
  isTemp?: boolean;
}

type TextPart = { type: "text"; text: string };
type ToolPart = {
  type: "tool-invocation";
  toolCallId: string;
  toolName: string;
  state: "call" | "result" | "partial-call";
  args?: Record<string, unknown>;
  result?: unknown;
};

type ScreenshotResult = {
  imageUrl: string;
  url: string;
  breakpoint: string;
  fullPage?: boolean;
  watermark?: { text: string; capturedAt: string };
};

type TableResult = {
  type: "table";
  title?: string;
  headers: string[];
  rows: string[][];
  caption?: string;
};

type DomainRankResult = {
  type: "domain_rank";
  domain: string;
  /** False when Peersheep has never measured this domain */
  measured?: boolean;
  domainAuthority: number | null;
  globalRank: number | null;
  organicTraffic?: number | null;
  organicKeywords?: number | null;
  mobileScore?: number | null;
  desktopScore?: number | null;
};
type MessagePart = TextPart | ToolPart | { type: string; [key: string]: unknown };

function getText(msg: ChatMessage): string {
  if (msg.parts && msg.parts.length > 0) {
    return msg.parts
      .filter(p => p.type === "text")
      .map(p => (p as TextPart).text ?? "")
      .join("");
  }
  if (typeof msg.content === "string") return msg.content;
  return "";
}

function getToolParts(msg: ChatMessage): ToolPart[] {
  if (!msg.parts) return [];
  return msg.parts
    .filter(p =>
      p.type === "tool-invocation" ||
      p.type === "tool_use" ||
      p.type === "tool-call" ||
      p.type === "tool_call" ||
      p.type === "tool"
    )
    .map(p => {
      // Normalize to ToolPart shape regardless of SDK version
      const raw = p as Record<string, unknown>;
      return {
        type: "tool-invocation" as const,
        toolCallId: (raw.toolCallId ?? raw.id ?? raw.tool_call_id ?? "") as string,
        toolName: (raw.toolName ?? raw.name ?? raw.tool_name ?? "") as string,
        state: (raw.state ?? (raw.result !== undefined ? "result" : "call")) as ToolPart["state"],
        args: (raw.args ?? raw.input ?? raw.parameters ?? {}) as Record<string, unknown>,
        result: raw.result ?? raw.output,
      } satisfies ToolPart;
    })
    .filter(p => p.toolName !== "");
}

function toolToThoughtStep(part: ToolPart, index: number): ThoughtStep {
  const meta = TOOL_META[part.toolName];
  const args = part.args ?? {};
  const detail = typeof args.url === "string"
    ? args.url.replace(/^https?:\/\/(www\.)?/, "").split("/")[0]
    : undefined;
  return {
    id: part.toolCallId || `tool-${index}`,
    label: meta?.label(args) ?? part.toolName.replace(/_/g, " "),
    icon: meta?.icon,
    status: part.state === "result" ? "complete" : "active",
    detail,
  };
}

// ─── Tool call card ───────────────────────────────────────────────────────────

const TOOL_META: Record<string, { icon: string; label: (args: Record<string, unknown>) => string }> = {
  scrape_page: {
    icon: "radar-01",
    label: (args) => `Scraping ${String(args.url ?? "").replace(/^https?:\/\/(www\.)?/, "").split("/")[0]}`,
  },
  take_screenshot: {
    icon: "camera-01",
    label: (args) => {
      const domain = String(args.url ?? "").replace(/^https?:\/\/(www\.)?/, "").split("/")[0];
      const bp = String(args.breakpoint ?? "desktop");
      const fp = args.fullPage ? " · full page" : "";
      return `Screenshot of ${domain} (${bp}${fp})`;
    },
  },
  render_table: {
    icon: "table-01",
    label: (args) => args.title ? `Table: ${args.title}` : "Rendering table",
  },
  render_domain_rank: {
    icon: "chart-bar-01",
    label: (args) => args.domain ? `Domain rank: ${String(args.domain)}` : "Domain ranking",
  },
  get_recent_changes: {
    icon: "activity-01",
    label: (args) => `Checking detected changes${args.domain ? ` on ${String(args.domain)}` : ""}`,
  },
};

function ToolCard({ part }: { part: ToolPart }) {
  const meta = TOOL_META[part.toolName];
  const icon = meta?.icon ?? "magic-wand-01";
  const label = meta?.label(part.args ?? {}) ?? part.toolName.replace(/_/g, " ");
  const isDone = part.state === "result";
  const isError = isDone && part.result && typeof part.result === "object" && "error" in (part.result as object);

  const screenshotResult = isDone && part.toolName === "take_screenshot" && !isError
    ? (part.result as ScreenshotResult)
    : null;

  const tableResult = isDone && part.toolName === "render_table" && !isError
    ? (part.result as TableResult)
    : null;

  const domainRankResult = isDone && part.toolName === "render_domain_rank" && !isError
    ? (part.result as DomainRankResult)
    : null;

  return (
    <div className="space-y-2">
      {/* Status pill */}
      <div className={cn(
        "inline-flex items-center gap-2.5 rounded-lg border px-3 py-2 text-[11px] font-mono transition-all",
        isError
          ? "border-destructive/20 bg-destructive/5 text-destructive"
          : isDone
          ? "border-border bg-muted/30 text-muted-foreground"
          : "border-border bg-muted/50 text-foreground"
      )}>
        {!isDone ? (
          <Icon name="refresh" size={11} className="animate-spin shrink-0 text-muted-foreground" />
        ) : isError ? (
          <Icon name="alert-circle" size={11} className="shrink-0" />
        ) : (
          <Icon name={icon} size={11} className="shrink-0 text-muted-foreground" />
        )}
        <span>{label}</span>
        {isDone && !isError && (
          <Icon name="tick-01" size={10} className="shrink-0 text-green-500" />
        )}
      </div>

      {/* Screenshot result with watermark */}
      {screenshotResult && (
        <div className="rounded-lg border border-border overflow-hidden max-w-sm shadow-sm">
          {/* Image with CSS watermark overlay */}
          <div className="relative">
            <img
              src={screenshotResult.imageUrl}
              alt={label}
              className="w-full block"
              loading="lazy"
            />
            {/* Watermark overlay */}
            <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-2.5 py-1.5"
              style={{ background: "linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 100%)" }}>
              <span className="font-mono text-[11px] text-white/80 tracking-widest uppercase">
                peersheep.com
              </span>
              <span className="font-mono text-[11px] text-white/60">
                {screenshotResult.breakpoint}
                {screenshotResult.fullPage ? " · full" : ""}
              </span>
            </div>
          </div>
          {/* Footer */}
          <div className="px-3 py-1.5 border-t border-border bg-muted/30 flex items-center gap-2">
            <Icon name="camera-01" size={10} className="text-muted-foreground shrink-0" />
            <span className="font-mono text-[11px] text-muted-foreground truncate flex-1">
              {String(screenshotResult.url ?? "").replace(/^https?:\/\/(www\.)?/, "")}
            </span>
            <a href={screenshotResult.imageUrl} target="_blank" rel="noopener noreferrer" className="shrink-0">
              <Icon name="link-01" size={10} className="text-muted-foreground hover:text-foreground transition-colors" />
            </a>
          </div>
        </div>
      )}

      {/* Table result */}
      {tableResult && <TableCard table={tableResult} />}

      {/* Domain rank result */}
      {domainRankResult && <DomainRankCard data={domainRankResult} />}
    </div>
  );
}

// ─── Domain rank card ─────────────────────────────────────────────────────────

function DomainRankCard({ data }: { data: DomainRankResult }) {
  if (data.measured === false) {
    return (
      <p className="max-w-sm rounded-lg border border-border bg-muted/30 px-3 py-2 font-sans text-xs text-muted-foreground">
        Peersheep hasn't measured {data.domain} yet. Run Know your competitors to fetch its rank, traffic and page speed.
      </p>
    );
  }
  const daScore = data.domainAuthority;
  const daPercent = daScore != null ? (daScore / 10) * 100 : null;

  // Color based on DA score
  const daColor =
    daScore == null ? "text-muted-foreground" :
    daScore >= 7 ? "text-green-500" :
    daScore >= 4 ? "text-yellow-500" :
    "text-orange-500";

  const daBarColor =
    daScore == null ? "bg-muted" :
    daScore >= 7 ? "bg-green-500" :
    daScore >= 4 ? "bg-yellow-500" :
    "bg-orange-500";

  return (
    <div className="rounded-lg border border-border overflow-hidden max-w-sm">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-muted/30">
        <img
          src={`https://www.google.com/s2/favicons?domain=${data.domain}&sz=16`}
          alt=""
          className="h-3.5 w-3.5 rounded shrink-0"
        />
        <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground flex-1 truncate">
          {data.domain}
        </span>
        <Icon name="chart-bar-01" size={10} className="text-muted-foreground shrink-0" />
      </div>

      <div className="p-3 space-y-3">
        {/* Domain Authority */}
        {daScore != null && (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="font-mono text-[11px] text-muted-foreground uppercase tracking-widest">Domain Authority</span>
              <span className={cn("font-mono text-sm font-bold tabular-nums", daColor)}>
                {daScore.toFixed(2)}<span className="text-[11px] font-normal text-muted-foreground">/10</span>
              </span>
            </div>
            <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
              <div
                className={cn("h-full rounded-full transition-all", daBarColor)}
                style={{ width: `${daPercent}%` }}
              />
            </div>
          </div>
        )}

        {/* Stats grid */}
        <div className="grid grid-cols-2 gap-2">
          {data.globalRank != null && (
            <div className="rounded-md border border-border bg-muted/20 px-2.5 py-2">
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Global Rank</p>
              <p className="font-mono text-xs font-semibold tabular-nums">#{data.globalRank.toLocaleString()}</p>
            </div>
          )}
          {data.organicTraffic != null && (
            <div className="rounded-md border border-border bg-muted/20 px-2.5 py-2">
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Organic Traffic</p>
              <p className="font-mono text-xs font-semibold tabular-nums">
                {data.organicTraffic >= 1_000_000
                  ? `${(data.organicTraffic / 1_000_000).toFixed(1)}M`
                  : data.organicTraffic >= 1_000
                  ? `${(data.organicTraffic / 1_000).toFixed(1)}k`
                  : data.organicTraffic.toLocaleString()}
                <span className="font-normal text-muted-foreground">/mo</span>
              </p>
            </div>
          )}
          {data.organicKeywords != null && (
            <div className="rounded-md border border-border bg-muted/20 px-2.5 py-2">
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Keywords</p>
              <p className="font-mono text-xs font-semibold tabular-nums">
                {data.organicKeywords >= 1_000
                  ? `${(data.organicKeywords / 1_000).toFixed(1)}k`
                  : data.organicKeywords.toLocaleString()}
              </p>
            </div>
          )}
          {(data.mobileScore != null || data.desktopScore != null) && (
            <div className="rounded-md border border-border bg-muted/20 px-2.5 py-2">
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Page Speed</p>
              <p className="font-mono text-xs font-semibold tabular-nums">
                {data.mobileScore != null ? data.mobileScore : "—"}
                <span className="font-normal text-muted-foreground"> mob</span>
                {" · "}
                {data.desktopScore != null ? data.desktopScore : "—"}
                <span className="font-normal text-muted-foreground"> desk</span>
              </p>
            </div>
          )}
        </div>
      </div>

    </div>
  );
}

// ─── Table card ───────────────────────────────────────────────────────────────

function TableCard({ table }: { table: TableResult }) {
  const [sortCol, setSortCol] = useState<number | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [copied, setCopied] = useState(false);

  const sortedRows = sortCol === null ? table.rows : [...table.rows].sort((a, b) => {
    const av = a[sortCol] ?? "";
    const bv = b[sortCol] ?? "";
    return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
  });

  const handleSort = (i: number) => {
    if (sortCol === i) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(i); setSortDir("asc"); }
  };

  const copyCSV = () => {
    const csv = [table.headers, ...table.rows].map(r => r.map(c => `"${c.replace(/"/g, '""')}"`).join(",")).join("\n");
    navigator.clipboard.writeText(csv);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const downloadCSV = () => {
    const csv = [table.headers, ...table.rows].map(r => r.map(c => `"${c.replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(table.title ?? "table").toLowerCase().replace(/\s+/g, "-")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="rounded-lg border border-border overflow-hidden max-w-2xl">
      {/* Header */}
      {table.title && (
        <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-muted/30">
          <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">{table.title}</span>
          <div className="flex items-center gap-2">
            <button onClick={copyCSV} className="flex items-center gap-1 font-mono text-[11px] text-muted-foreground hover:text-foreground transition-colors">
              <Icon name={copied ? "tick-01" : "copy-01"} size={9} />
              {copied ? "Copied" : "Copy"}
            </button>
            <button onClick={downloadCSV} className="flex items-center gap-1 font-mono text-[11px] text-muted-foreground hover:text-foreground transition-colors">
              <Icon name="download-01" size={9} />
              CSV
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead className="border-b border-border bg-muted/20">
            <tr>
              {table.headers.map((h, i) => (
                <th
                  key={i}
                  onClick={() => handleSort(i)}
                  className="text-left px-3 py-2 font-mono text-[11px] uppercase tracking-widest text-muted-foreground cursor-pointer hover:text-foreground transition-colors select-none whitespace-nowrap"
                >
                  <span className="flex items-center gap-1">
                    {h}
                    {sortCol === i && (
                      <Icon name={sortDir === "asc" ? "arrow-up-01" : "arrow-down-01"} size={8} />
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row, ri) => (
              <tr key={ri} className="border-b border-border/50 last:border-0 hover:bg-muted/20 transition-colors">
                {table.headers.map((_, ci) => (
                  <td key={ci} className="px-3 py-2 font-sans text-foreground/80 whitespace-nowrap">
                    {row[ci] ?? "—"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Caption */}
      {table.caption && (
        <div className="px-3 py-2 border-t border-border bg-muted/10">
          <p className="font-sans text-xs text-muted-foreground">{table.caption}</p>
        </div>
      )}
    </div>
  );
}

// ─── Action button ────────────────────────────────────────────────────────────

function ActionBtn({ icon, label, onClick }: { icon: string; label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      title={label}
      className="flex items-center justify-center h-6 w-6 rounded hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
    >
      <Icon name={icon} size={12} />
    </button>
  );
}

// ─── User bubble ──────────────────────────────────────────────────────────────

function UserBubble({
  text,
  onEdit,
  onCopy,
  onRetry,
}: {
  text: string;
  onEdit: (newText: string) => void;
  onCopy: () => void;
  onRetry: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(text);
  const [copied, setCopied] = useState(false);
  const taRef = useRef<HTMLTextAreaElement>(null);

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    onCopy();
  };

  const handleSaveEdit = () => {
    if (draft.trim() && draft.trim() !== text) {
      onEdit(draft.trim());
    }
    setEditing(false);
  };

  useEffect(() => {
    if (editing && taRef.current) {
      taRef.current.focus();
      taRef.current.style.height = "auto";
      taRef.current.style.height = `${taRef.current.scrollHeight}px`;
    }
  }, [editing]);

  return (
    <div className="group flex flex-col items-end gap-1">
      {editing ? (
        <div className="w-full max-w-[75%] space-y-2">
          <textarea
            ref={taRef}
            value={draft}
            onChange={e => {
              setDraft(e.target.value);
              e.target.style.height = "auto";
              e.target.style.height = `${e.target.scrollHeight}px`;
            }}
            onKeyDown={e => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSaveEdit(); }
              if (e.key === "Escape") { setEditing(false); setDraft(text); }
            }}
            className="w-full resize-none bg-muted text-foreground rounded-2xl rounded-tr-sm px-4 py-2.5 font-sans text-xs leading-relaxed outline-none border border-foreground/20 focus:border-foreground/40 scrollbar-hide"
            rows={1}
          />
          <div className="flex gap-2 justify-end">
            <button onClick={() => { setEditing(false); setDraft(text); }}
              className="font-mono text-[11px] text-muted-foreground hover:text-foreground transition-colors px-2 py-1 border border-border rounded">
              Cancel
            </button>
            <button onClick={handleSaveEdit}
              className="font-mono text-[11px] bg-foreground text-background px-2 py-1 rounded hover:bg-foreground/90 transition-colors">
              Send
            </button>
          </div>
        </div>
      ) : (
        <div className="max-w-[75%] bg-muted text-foreground rounded-2xl rounded-tr-sm px-4 py-2.5">
          <p className="font-sans text-xs leading-relaxed whitespace-pre-wrap">{text}</p>
        </div>
      )}

      {/* Actions — visible on hover */}
      {!editing && (
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <ActionBtn icon={copied ? "tick-01" : "copy-01"} label="Copy" onClick={handleCopy} />
          <ActionBtn icon="edit-01" label="Edit" onClick={() => { setDraft(text); setEditing(true); }} />
          <ActionBtn icon="refresh" label="Retry" onClick={onRetry} />
        </div>
      )}
    </div>
  );
}

// ─── Assistant bubble ─────────────────────────────────────────────────────────

function AssistantBubble({
  msg,
  isStreaming,
  onCopy,
  onRetry,
}: {
  msg: ChatMessage;
  isStreaming?: boolean;
  onCopy: () => void;
  onRetry: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const text = getText(msg);
  const toolParts = getToolParts(msg);

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    onCopy();
  };

  if (isStreaming && !text && toolParts.length === 0) {
    return (
      <div className="flex justify-start px-1 py-2">
        <SquareLoader label="Thinking" />
      </div>
    );
  }

  return (
    <div className="group flex flex-col items-start gap-1">
      <div className="max-w-[85%] space-y-3">
        {/* Tool call cards */}
        {toolParts.length > 0 && (
          <div className="space-y-2">
            <ChainOfThought steps={toolParts.map(toolToThoughtStep)} />
            {toolParts.map(tp => <ToolCard key={tp.toolCallId} part={tp} />)}
          </div>
        )}

        {/* Text response */}
        {text && (
          <div className="font-sans text-xs leading-relaxed">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({ children }) => <h1 className="font-mono text-lg font-bold tracking-tight mt-5 mb-2 first:mt-0">{children}</h1>,
                h2: ({ children }) => <h2 className="font-mono text-base font-semibold tracking-tight mt-4 mb-1.5 first:mt-0">{children}</h2>,
                h3: ({ children }) => <h3 className="font-mono text-xs font-semibold tracking-tight mt-3 mb-1 first:mt-0 text-muted-foreground">{children}</h3>,
                p: ({ children }) => <p className="mb-2 last:mb-0 leading-relaxed">{children}</p>,
                strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
                em: ({ children }) => <em className="italic opacity-80">{children}</em>,
                a: ({ href, children }) => (
                  <a href={href} target="_blank" rel="noopener noreferrer"
                    className="text-foreground underline underline-offset-2 decoration-foreground/30 hover:decoration-foreground transition-colors">
                    {children}
                  </a>
                ),
                ul: ({ children }) => <ul className="mb-2 space-y-0.5 pl-4 list-disc marker:text-muted-foreground">{children}</ul>,
                ol: ({ children }) => <ol className="mb-2 space-y-0.5 pl-4 list-decimal marker:text-muted-foreground">{children}</ol>,
                li: ({ children }) => <li className="leading-relaxed">{children}</li>,
                code: ({ children, className }) => {
                  const isBlock = className?.includes("language-");
                  return isBlock
                    ? <code className="block bg-muted rounded-lg px-3 py-2 font-mono text-[11px] overflow-x-auto my-2">{children}</code>
                    : <code className="font-mono text-[11px] bg-muted px-1.5 py-0.5 rounded">{children}</code>;
                },
                pre: ({ children }) => <pre className="bg-muted rounded-lg p-3 overflow-x-auto my-2 font-mono text-[11px]">{children}</pre>,
                blockquote: ({ children }) => (
                  <blockquote className="border-l-2 border-border pl-3 my-2 text-muted-foreground italic">{children}</blockquote>
                ),
                table: ({ children }) => (
                  <div className="overflow-x-auto my-3">
                    <table className="w-full text-[11px] border-collapse">{children}</table>
                  </div>
                ),
                thead: ({ children }) => <thead className="border-b border-border">{children}</thead>,
                th: ({ children }) => <th className="text-left px-3 py-1.5 font-semibold">{children}</th>,
                td: ({ children }) => <td className="px-3 py-1.5 border-b border-border/50 text-foreground/80">{children}</td>,
                hr: () => <hr className="border-border my-3" />,
                img: ({ src, alt }) => (
                  <div className="my-3">
                    <img src={src} alt={alt ?? "screenshot"} className="rounded-lg border border-border max-w-full shadow-sm" loading="lazy" />
                    {alt && <p className="mt-1 font-mono text-[11px] text-muted-foreground">{alt}</p>}
                  </div>
                ),
              }}
            >
              {text}
            </ReactMarkdown>
          </div>
        )}
      </div>

      {/* Actions */}
      {!isStreaming && (text || toolParts.length > 0) && (
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          {text && <ActionBtn icon={copied ? "tick-01" : "copy-01"} label="Copy" onClick={handleCopy} />}
          <ActionBtn icon="refresh" label="Regenerate" onClick={onRetry} />
        </div>
      )}
    </div>
  );
}

// ─── ChatMessages ─────────────────────────────────────────────────────────────

interface ChatMessagesProps {
  messages: ChatMessage[];
  streamingMsg: { id: string; text: string } | null;
  status: "idle" | "streaming" | "error";
  liveTools?: Array<{ toolName: string; args: Record<string, unknown>; done: boolean }>;
  onEditMessage?: (messageId: string, newText: string) => void;
  onRetry?: (messageId: string, role: "user" | "assistant") => void;
}

export function ChatMessages({ messages, streamingMsg, status, liveTools = [], onEditMessage, onRetry }: ChatMessagesProps) {
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamingMsg]);

  const confirmedIds = new Set(messages.map(m => m.id));
  const showStreaming = streamingMsg && !confirmedIds.has(streamingMsg.id);
  const showThinking = status === "streaming" && !streamingMsg;

  const liveThoughts = liveTools.map((tool, index) => toolToThoughtStep({
    type: "tool-invocation",
    toolCallId: `live-${index}`,
    toolName: tool.toolName,
    state: tool.done ? "result" : "call",
    args: tool.args,
  }, index));

  return (
    <div className="h-full overflow-y-auto scrollbar-hide px-6 pt-6 pb-2">
      <div className="max-w-3xl mx-auto space-y-5">
        {messages.map((msg) => {
          const text = getText(msg);
          return msg.role === "user" ? (
            <UserBubble
              key={msg.id}
              text={text}
              onCopy={() => {}}
              onEdit={(newText) => onEditMessage?.(msg.id, newText)}
              onRetry={() => onRetry?.(msg.id, "user")}
            />
          ) : (
            <AssistantBubble
              key={msg.id}
              msg={msg}
              onCopy={() => {}}
              onRetry={() => onRetry?.(msg.id, "assistant")}
            />
          );
        })}

        {showStreaming && (
          <AssistantBubble
            key={streamingMsg.id}
            msg={{ id: streamingMsg.id, role: "assistant", parts: [{ type: "text", text: streamingMsg.text }], isStreaming: true }}
            isStreaming
            onCopy={() => {}}
            onRetry={() => {}}
          />
        )}

        {showThinking && (
          <div className="flex justify-start">
            {liveThoughts.length > 0
              ? <ChainOfThought steps={liveThoughts} />
              : <SquareLoader label={null} />}
          </div>
        )}

        {/* Live tools during streaming (before thinking dots appear) */}
        {!showThinking && liveTools.length > 0 && status === "streaming" && (
          <div className="flex justify-start">
            <ChainOfThought steps={liveThoughts} />
          </div>
        )}

        <div ref={endRef} />
      </div>
    </div>
  );
}
